import React, { useState } from 'react';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import useBundleTranslation from 'i18n';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { Box, Button, FormControl, FormLabel, Grid, TextField } from '@mui/material';
import { ActSubmit } from 'components/common/form/act';
import { formAPI } from 'api/form';
import { createPortal } from 'react-dom';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useQueryClient } from '@tanstack/react-query';
import StaticInfo from 'components/common/StaticInfo';
import IconMi from 'components/common/icon/IconMi';
import { downloadAPI } from 'api/api';

export default function ApiTokenForm(props: IFormLayoutProps) {
    const { t } = useBundleTranslation(['app/editor/api-token']);
    const queryClient = useQueryClient();

    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState('');

    const handleSubmit = async (data: any) => {
        setLoading(true);

        return formAPI
            .saveFormData(`data/editor/api-token/{tokenId}/form`, data, props.hookForm.form)
            .catch((error) => {
                alert(error);
                onPopupHide();
            })
            .finally(() => setLoading(false));
    };

    props.hookForm.form.formAct.overrideAct(
        'submit',
        class extends ActSubmit {
            async exec(data: any): Promise<any> {
                handleSubmit(data).then((response) => {
                    if (response.data.status === 'OK' && response.data.data.newApiToken) {
                        setToken(response.data.data.newApiToken);
                    } else {
                        props.hookForm.form.formAct.act.afterSaveError.exec(response);
                    }
                });
            }
        },
    );

    const popupSettings: PopupSettings = {
        title: t('token_popup.generated'),
        textOK: t('token_popup.download'),
        iconOK: 'download',
        maxWidth: 'popupSm',
    };

    const onPopupHide = () => {
        queryClient.invalidateQueries({ queryKey: ['apiTokenGrid'] }).then(() => {
            setToken('');
            props.asPopupProps.onHide();
        });
    };

    const downloadAction = () => {
        downloadAPI.export.getDownload(`data/editor/api-token/download`, { token }).then(() => {
            onPopupHide();
        });
    };

    return (
        <>
            <FormLayoutComposer props={props} />
            <Popup
                settings={popupSettings}
                open={token > ''}
                onHide={onPopupHide}
                onConfirm={downloadAction}
                maxWidth={'sm'}
            >
                <Box sx={{ margin: '-16px -24px 24px ' }}>
                    <StaticInfo label={t('token_popup.info')} />
                </Box>
                <FormControl fullWidth>
                    <FormLabel>{t('token_popup.api_token')}</FormLabel>
                    <Grid container spacing={1}>
                        <Grid item xs={10}>
                            <TextField value={token} disabled={true} fullWidth />
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                variant={'light'}
                                startIcon={<IconMi icon="copy" />}
                                onClick={() => {
                                    navigator.clipboard.writeText(token);
                                }}
                            >
                                {t('token_popup.copy')}
                            </Button>
                        </Grid>
                    </Grid>
                </FormControl>
            </Popup>
            {loading &&
                createPortal(
                    <LoadingPlaceholder
                        sx={{
                            position: 'absolute',
                            zIndex: (theme) => 9999,
                        }}
                    />,
                    document.body,
                )}
        </>
    );
}
